import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Lock, Send, CheckCircle2, Save, MessageSquareWarning } from "lucide-react";

const eur = (n) => (n == null || n === "" ? "—" : new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n));
const SECTIONS = [
  ["city", "Ville", (b) => b.city], ["highlights", "Points forts", (b) => (b.highlights || []).join(" · ")],
  ["full_description", "Présentation détaillée", (b) => b.full_description], ["revenue", "Chiffre d'affaires", (b) => eur(b.revenue)],
  ["ebitda", "EBE / EBITDA", (b) => eur(b.ebitda)], ["net_result", "Résultat net", (b) => eur(b.net_result)],
  ["employees", "Effectif", (b) => b.employees], ["year_founded", "Année de création", (b) => b.year_founded],
  ["asking_price", "Prix de cession", (b) => eur(b.asking_price)], ["real_estate_price", "Foncier", (b) => eur(b.real_estate_price)],
  ["video_url", "Vidéo", (b) => b.video_url], ["gallery", "Galerie photos", (b) => b.gallery],
];
const Q_LABELS = { activity: "Activité", clients: "Clientèle", market: "Marché", strengths: "Atouts", growth: "Perspectives", reason_for_sale: "Raison de la cession" };
const ACTION_LABEL = { publish: "Publiée", request_changes: "Modifications demandées", save: "Message" };

const SectionValue = ({ k, v }) => {
  if (k === "gallery") return v?.length ? <div className="flex gap-2 flex-wrap">{v.map((u) => <img key={u} src={u} alt="" className="w-20 h-14 object-cover border" />)}</div> : <span className="text-gray-400">—</span>;
  if (k === "video_url") return v ? <a href={v} target="_blank" rel="noreferrer" className="text-[#1B2A4A] underline break-all">{v}</a> : <span className="text-gray-400">—</span>;
  return <span className="whitespace-pre-line">{v === null || v === undefined || v === "" ? <span className="text-gray-400">—</span> : v}</span>;
};

const PrivateBlock = ({ b }) => {
  const c = b.questionnaire?.company;
  const q = b.questionnaire || {};
  return (
    <div className="bg-[#F8F6F0] border border-[#E0C173]/40 p-4 space-y-2 text-sm" data-testid="review-private">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#8a6d22]"><Lock className="w-3.5 h-3.5" /> Informations privées — jamais publiées</div>
      {c?.siret && <div><strong>{c.name}</strong> · SIRET {c.siret}{c.legal_form && ` · ${c.legal_form}`}{c.naf_code && ` · NAF ${c.naf_code}`}</div>}
      {c?.address && <div>{c.address}</div>}
      {c?.official?.dirigeants?.length > 0 && <div>Dirigeant(s) : {c.official.dirigeants.join(", ")}</div>}
      {Object.entries(Q_LABELS).filter(([k]) => q[k]).map(([k, l]) => <div key={k}><span className="text-gray-500">{l} : </span>{q[k]}</div>)}
      {!c && !Object.keys(Q_LABELS).some((k) => q[k]) && <div className="text-gray-500">Aucune donnée privée (fiche saisie par l'admin).</div>}
    </div>
  );
};

export default function BusinessReviewDialog({ businessId, onOpenChange, onDone }) {
  const [b, setB] = useState(null);
  const [sections, setSections] = useState({});
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!businessId) return;
    setB(null); setMessage("");
    api.get(`/businesses/${businessId}`).then(({ data }) => {
      setB(data);
      setSections(Object.fromEntries(SECTIONS.map(([k]) => [k, data.published_sections?.[k] !== false])));
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [businessId]);

  const send = async (action) => {
    if (action === "request_changes" && !message.trim()) return toast.error("Précisez au cédant les modifications attendues.");
    setBusy(action);
    try {
      await api.post(`/admin/businesses/${businessId}/review`, { action, published_sections: sections, message });
      toast.success({ publish: "Fiche publiée sur le site", request_changes: "Demande de modifications envoyée au cédant", save: "Choix de publication enregistrés" }[action]);
      onDone?.(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy("");
  };

  return (
    <Dialog open={!!businessId} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#1B2A4A]">{b ? b.title : "Chargement…"}</DialogTitle>
          <DialogDescription>{b && `${b.sector} · ${b.region} · statut : ${b.status === "available" ? "publiée" : b.status === "pending" ? "en attente de validation" : b.status}`}</DialogDescription>
        </DialogHeader>
        {b && (
          <div className="space-y-6">
            {b.hero_image && <img src={b.hero_image} alt="" className="w-full h-48 object-cover" />}
            <div><div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Teaser public (toujours publié)</div><p className="text-sm text-[#1B2A4A]">{b.short_description}</p></div>
            <PrivateBlock b={b} />
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-gray-500">Éléments à publier sur le site</div>
                <div className="text-xs space-x-3"><button data-testid="review-check-all" onClick={() => setSections(Object.fromEntries(SECTIONS.map(([k]) => [k, true])))} className="underline">Tout cocher</button><button data-testid="review-uncheck-all" onClick={() => setSections(Object.fromEntries(SECTIONS.map(([k]) => [k, false])))} className="underline">Tout décocher</button></div>
              </div>
              <div className="divide-y border border-gray-200">
                {SECTIONS.map(([k, label, get]) => (
                  <label key={k} className={`flex gap-3 p-3 text-sm cursor-pointer ${sections[k] ? "bg-white" : "bg-gray-50 text-gray-500"}`}>
                    <Checkbox data-testid={`review-section-${k}`} checked={!!sections[k]} onCheckedChange={(v) => setSections((s) => ({ ...s, [k]: !!v }))} className="mt-0.5" />
                    <div className="flex-1 min-w-0"><div className="font-medium text-[#1B2A4A]">{label} {!sections[k] && <span className="text-xs font-normal text-gray-500">(masqué)</span>}</div><div className="mt-0.5 text-[#475569] line-clamp-4"><SectionValue k={k} v={get(b)} /></div></div>
                  </label>
                ))}
              </div>
            </div>
            {b.admin_messages?.length > 0 && (
              <div data-testid="review-history"><div className="text-xs uppercase tracking-wider text-gray-500 mb-2">Historique des échanges</div>
                <ol className="space-y-2 border-l-2 border-[#E0C173]/50 pl-4 text-sm">{b.admin_messages.map((m) => <li key={m.message_id}><span className="text-xs text-gray-500">{new Date(m.created_at).toLocaleString("fr-FR")} · {ACTION_LABEL[m.action]} · {m.author_name}</span>{m.text && <p className="whitespace-pre-line">{m.text}</p>}</li>)}</ol>
              </div>
            )}
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500 mb-1.5">Message au cédant (envoyé par email et visible dans son espace)</div>
              <Textarea data-testid="review-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Ex. : Bravo, votre fiche est en ligne ! / Merci d'ajouter des photos de l'atelier…" className="rounded-none border-gray-300" />
            </div>
            <div className="flex flex-wrap gap-2 justify-end border-t pt-4">
              <button data-testid="review-save" onClick={() => send("save")} disabled={!!busy} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm border border-gray-300 hover:border-[#1B2A4A]"><Save className="w-4 h-4" /> Enregistrer</button>
              <button data-testid="review-request-changes" onClick={() => send("request_changes")} disabled={!!busy} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm border border-amber-300 text-amber-800 hover:bg-amber-50"><MessageSquareWarning className="w-4 h-4" /> Demander des modifications</button>
              <button data-testid="review-publish" onClick={() => send("publish")} disabled={!!busy} className="inline-flex items-center gap-1.5 px-5 py-2 text-sm bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-semibold">{busy === "publish" ? <Send className="w-4 h-4 animate-pulse" /> : <CheckCircle2 className="w-4 h-4" />} {b.status === "available" ? "Mettre à jour la publication" : "Publier"}</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
